import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAppSelector } from 'hooks';
import { buyFreezes, isStreakNetworkError, newIdempotencyKey } from 'services/streaksService';

import { BottomSheet } from 'components/BottomSheet';
import { CashierContext } from 'components/Cashier/CashierContext';
import { useCompactLobbyHeader } from 'components/SaloonLobby/compactHeader';
import {
  streakStore,
  useStreakSnapshot,
  useStreakStore
} from 'components/Streaks/state/streakStore';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import BuyFreezesCard from './BuyFreezesCard';
import { PaySource } from './payment';

import styles from './BuyFreezesSheet.module.scss';

const toNumber = (v: unknown) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'));
  return Number.isFinite(n) ? n : 0;
};

/** Offline: nothing went out, so nothing was bought. */
export const OFFLINE_MESSAGE =
  "No internet connection. Nothing was bought. Try again when you're back online.";
/** Sent but no answer: say nothing either way about a charge; the retry reuses the key. */
export const UNREACHABLE_MESSAGE =
  "Couldn't reach the server. Check your connection and try again.";

const buyErrorMessage = (e: unknown) => {
  if (isStreakNetworkError(e)) return e.sent ? UNREACHABLE_MESSAGE : OFFLINE_MESSAGE;
  return (e instanceof Error && e.message) || 'Could not buy freezes. Try again.';
};

/**
 * Buy freezes (EN-11236; $2, 1 to 3 at a time, at most 3 bought held): a bottom sheet up to 700 px wide (or 500 px tall),
 * a centred 440 px dialog above that. Opened from the panel's Freezes row via
 * the streak store. One idempotency key per open sheet, kept across retries
 * of a failed purchase so a double tap or a retry charges once.
 *
 * `onLeave` closes the host (the account panel) when Add funds hands off to
 * the Cashier, which would otherwise open underneath it, as the panel's own
 * Cashier row does.
 */
export default function BuyFreezesSheet({ onLeave }: { onLeave?: () => void } = {}) {
  const open = useStreakStore((s) => s.buyOpen);
  const snapshot = useStreakSnapshot();
  const phone = useCompactLobbyHeader();
  const reducedMotion = useStreakReducedMotion();
  const promo = toNumber(useAppSelector((s) => s.user.playerChargeBalance));
  const cash = toNumber(useAppSelector((s) => s.user.playerBankAmount));
  const { openCashier, setOpenedFromMenu } = useContext(CashierContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{
    quantity: number;
    source: PaySource;
    heldAfter: number;
    boughtAfter: number;
  } | null>(null);
  const keyRef = useRef<string>('');

  useEffect(() => {
    if (open) {
      keyRef.current = newIdempotencyKey();
      setBusy(false);
      setError('');
      setDone(null);
    }
  }, [open]);

  const close = useCallback(() => streakStore.setBuyOpen(false), []);

  const onBuy = useCallback((quantity: number, source: PaySource) => {
    setBusy(true);
    setError('');
    buyFreezes({ quantity, source, idempotencyKey: keyRef.current })
      .then((snap) => {
        streakStore.accept(snap);
        setDone({
          quantity,
          source,
          heldAfter: snap.freezeBalance,
          boughtAfter: snap.promoFreezes + snap.cashFreezes
        });
      })
      .catch((e: unknown) => setError(buyErrorMessage(e)))
      .finally(() => setBusy(false));
  }, []);

  const onAddFunds = useCallback(
    (shortfall: number) => {
      close();
      // Back from the Cashier returns to the account panel, as from its Cashier row.
      setOpenedFromMenu(!!onLeave);
      onLeave?.();
      openCashier('deposit', Math.ceil(shortfall * 100) / 100);
    },
    [close, onLeave, openCashier, setOpenedFromMenu]
  );

  if (!snapshot) return null;
  const card = (
    <BuyFreezesCard
      bought={snapshot.promoFreezes + snapshot.cashFreezes}
      promo={promo}
      cash={cash}
      busy={busy}
      error={error}
      done={done}
      onBuy={onBuy}
      onAddFunds={onAddFunds}
      onClose={close}
    />
  );

  if (phone) {
    return (
      <BottomSheet
        open={open}
        onClose={close}
        testId='buy-freezes-sheet'
        maxWidth={540}
        ariaLabelledBy='buy-freezes-title'>
        {/* BottomSheet is the dialog (role, aria-modal, the title's id). */}
        <div className={styles.sheetBody}>{card}</div>
      </BottomSheet>
    );
  }

  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className={`${styles.backdrop} ${reducedMotion ? styles.still : ''}`}
      onClick={close}
      data-testid='buy-freezes-dialog'>
      <section
        className={styles.dialog}
        role='dialog'
        aria-modal='true'
        aria-labelledby='buy-freezes-title'
        onClick={(e) => e.stopPropagation()}>
        {card}
      </section>
    </div>,
    document.body
  );
}
